import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatGNF } from "@/constants/mcevents";
import { CalendarDays, MapPin, Ticket, Minus, Plus, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { imgSrc } from "@/lib/utils";
import { ShareButton } from "@/components/ShareButton";
import { toast } from "sonner";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1200&q=80";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [cart, setCart] = useState({});
  const [buyerName, setBuyerName] = useState(user?.name || "");
  const [buyerEmail, setBuyerEmail] = useState(user?.email || "");
  const [buyerPhone, setBuyerPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/events/${id}`).then((r) => setEvent(r.data.event)).catch(() => setEvent(false));
  }, [id]);

  useEffect(() => {
    if (user) {
      setBuyerName((v) => v || user.name || "");
      setBuyerEmail((v) => v || user.email || "");
      setBuyerPhone((v) => v || user.phone || "");
    }
  }, [user]);

  if (event === null) return <div className="container-mc py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;
  if (event === false) return <div className="container-mc py-24 text-center" data-testid="event-not-found"><p>Événement introuvable.</p><Link to="/events" className="btn-primary mt-6 inline-flex">Voir tous les événements</Link></div>;

  const dateStr = event.event_date
    ? new Date(event.event_date).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })
    : "";
  const items = event.ticket_types.filter((t) => t.active !== false);
  const total = items.reduce((sum, t) => sum + (cart[t.id] || 0) * t.price_gnf, 0);
  const totalCount = Object.values(cart).reduce((s, n) => s + n, 0);

  const setQty = (ttId, max, delta) => {
    setCart((c) => {
      const next = Math.max(0, Math.min(max, (c[ttId] || 0) + delta));
      return { ...c, [ttId]: next };
    });
  };

  const purchase = async () => {
    if (!user) { navigate("/login", { state: { from: `/events/${id}` } }); return; }
    if (totalCount === 0) { toast.error("Choisissez au moins un billet"); return; }
    if (!buyerName || !buyerEmail || !buyerPhone) { toast.error("Renseignez vos coordonnées"); return; }
    const items_payload = Object.entries(cart).filter(([, q]) => q > 0).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
    setBusy(true);
    try {
      const { data } = await api.post(`/events/${id}/purchase`, {
        buyer_name: buyerName, buyer_email: buyerEmail, buyer_phone: buyerPhone, items: items_payload,
      });
      if (data.payment_url) {
        toast.info(`Redirection vers le paiement sécurisé (${data.charged_amount} ${data.charged_currency})…`);
        window.location.href = data.payment_url;
        return;
      }
      toast.success(`${data.ticket_count} billet(s) acheté(s)`);
      navigate("/tickets");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="event-detail-page">
      <div className="relative aspect-[3/1] max-h-[440px] w-full overflow-hidden bg-black">
        <img src={imgSrc(event.cover_image) || HERO_FALLBACK} alt={event.title}
             className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 gn-strip-thick" />
      </div>
      <div className="container-mc -mt-32 relative z-10">
        <Link to="/events" className="inline-flex items-center text-xs font-mono bg-black/50 text-white hover:bg-black/70 px-3 py-1.5 rounded-full backdrop-blur-md mb-4 uppercase tracking-wider transition-colors" data-testid="back-to-events">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Événements
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="card-mc p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="overline mb-2">{event.artist_name}</p>
                  <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-[0.95]">{event.title}</h1>
                </div>
                <ShareButton
                  testid="share-event-btn"
                  url={`${process.env.REACT_APP_BACKEND_URL}/api/e/${event.id}`}
                  title={event.title}
                  text={`🎫 ${event.title} — ${event.artist_name}\n📅 ${dateStr}\n📍 ${event.venue}, ${event.city}\nRéservez vos billets sur MCZ EVENTS :`}
                  className="shrink-0 mt-1"
                />
              </div>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="border border-border p-4">
                  <p className="overline mb-1">Date</p>
                  <p className="font-bold capitalize inline-flex items-center gap-2"><CalendarDays className="w-4 h-4" /> {dateStr}</p>
                </div>
                <div className="border border-border p-4">
                  <p className="overline mb-1">Lieu</p>
                  <p className="font-bold inline-flex items-center gap-2"><MapPin className="w-4 h-4" /> {event.venue}</p>
                  <p className="text-xs text-muted-foreground mt-1">{event.city}</p>
                </div>
              </div>
              {event.description && (
                <div className="mt-6">
                  <p className="overline mb-2">À propos</p>
                  <p className="text-sm text-foreground/80 whitespace-pre-line leading-relaxed">{event.description}</p>
                </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="card-mc p-8 lg:sticky lg:top-6">
              <div className="flex items-center gap-2 mb-5">
                <Ticket className="w-5 h-5 text-[hsl(var(--primary))]" />
                <h2 className="font-display font-black text-xl tracking-tighter">Choisir vos billets</h2>
              </div>
              <div className="space-y-3">
                {items.length === 0 && <p className="text-sm text-muted-foreground">Aucun billet en vente pour le moment.</p>}
                {items.map((t) => {
                  const soldOut = t.available === 0;
                  const qty = cart[t.id] || 0;
                  return (
                    <div key={t.id} data-testid={`ticket-type-${t.id}`} className={`border border-border p-4 ${soldOut ? "opacity-50" : ""}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-sm">{t.name}</p>
                          {t.description && <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>}
                          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mt-1">{soldOut ? "Complet" : `${t.available} restants`}</p>
                        </div>
                        <p className="font-display font-black text-lg tracking-tighter">{formatGNF(t.price_gnf)}</p>
                      </div>
                      {!soldOut && (
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-border">
                            <button data-testid={`qty-minus-${t.id}`} onClick={() => setQty(t.id, t.available, -1)} className="p-1.5 hover:bg-muted disabled:opacity-40" disabled={qty === 0}>
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="px-4 text-sm font-bold min-w-[2rem] text-center" data-testid={`qty-${t.id}`}>{qty}</span>
                            <button data-testid={`qty-plus-${t.id}`} onClick={() => setQty(t.id, t.available, 1)} className="p-1.5 hover:bg-muted disabled:opacity-40" disabled={qty >= t.available || qty >= 20}>
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="font-bold text-sm">{formatGNF(qty * t.price_gnf)}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {totalCount > 0 && (
                <div className="mt-5 pt-5 border-t border-border space-y-3">
                  <input data-testid="buyer-name" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Nom complet" className="input-mc w-full" />
                  <input data-testid="buyer-email" type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} placeholder="Email" className="input-mc w-full" />
                  <input data-testid="buyer-phone" value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} placeholder="Téléphone" className="input-mc w-full" />
                </div>
              )}
              <div className="mt-5 pt-5 border-t border-border flex items-center justify-between">
                <div>
                  <p className="overline mb-0.5">Total</p>
                  <p className="font-display font-black text-2xl tracking-tighter">{formatGNF(total)}</p>
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">{totalCount} billet(s)</p>
                </div>
                <button data-testid="purchase-btn" onClick={purchase} disabled={busy || totalCount === 0}
                        className="btn-primary">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ticket className="w-4 h-4 mr-2" />}
                  {user ? "Payer" : "Se connecter"}
                </button>
              </div>
              <p className="mt-3 text-[10px] text-muted-foreground inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Paiement sécurisé via FedaPay · QR code envoyé après paiement</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
